import { AccordionIconButton, Box } from '@enechain/ecloud-designsystem'
import {
  FaChevronDownRegular,
  FaChevronRightRegular,
} from '@enechain/ecloud-icons'

import { iconSize } from '~/constants/iconSize'

/**
 * accordion の開閉標 (chevron)。 **`AccordionItemTrigger` の先頭 (= 左端)** に置く。
 *
 * ## なぜ `AccordionItemIndicator` ではなくこれか
 *
 * DS が accordion 用に**自前で持っている部品は `AccordionIconButton` と `useAccordion`
 * の 2 つだけ**で、`AccordionRoot` / `AccordionItem` / `AccordionItemTrigger` /
 * `AccordionItemIndicator` / `AccordionItemContent` は `@chakra-ui/react` の素通し
 * re-export (`dist/src/components/index.d.ts`)。 DS の theme にも accordion の
 * recipe / slotRecipe は無い。 つまり `AccordionItemIndicator` に DS の意匠は
 * 一切当たらない。
 *
 * 実際 `AccordionItemIndicator` の既定は **Chakra の `ChevronDownIcon` を開閉で
 * 180° 回転**させる形で、次の 2 点で正から外れる:
 *
 * - 8080 プロトは**グリフ差し替え** (閉 = chevron-right / 開 = chevron-down)。
 *   回転では絵が合わない (`design/css/components.css` `.ecl-accordion-trigger::before`)
 * - icon が `@enechain/ecloud-icons` 由来でない
 *
 * 対して DS `AccordionIconButton` は
 * `expanded === true ? FaChevronDownRegular : FaChevronRightRegular` を
 * `Object.midEmphasis` で描く ghost (= border 無し) の icon button で、
 * 8080 の指定と一致する。 デザイナー指定の Chromatic `disclosure-accordion` は
 * この component (DS 側 story の `meta.title` = `Disclosure/Accordion/AccordionIconButton VRT`)。
 *
 * ## [DS差異] `<button>` ではなく `<span>` として描く
 *
 * `AccordionItemTrigger` は ark の `<button>` を描くため、 その中に icon button
 * (= `<button>`) を入れると **button-in-button の DOM nesting 違反**になり、
 * 支援技術には「ボタンの中にボタン」 が読まれる。 本 repo は同じ理由で trigger 内に
 * 操作ボタンを置かない判断を既にしている (`SpotPrice/index.tsx` の overlay 実装)。
 * DS 自身の story は入れ子のまま置いているが、 ここでは `as="span"` で
 * **見た目はそのまま・要素だけ非 button** にして両立させる。
 *
 * `aria-hidden` にしているのも同じ理由:
 *
 * - 開閉の accessible name と `aria-expanded` は trigger 本体が持つので、
 *   chevron は装飾。 読み上げると同じ操作が 2 回案内される
 * - DS は `aria-label="accordion-button"` を**上書き不可**で固定しており
 *   (`Omit<…, 'aria-label'>`)、 そのままだと未翻訳の英語が読み上げられる
 */
export const AccordionDisclosureIcon = (): React.ReactElement => (
  <AccordionIconButton as="span" flexShrink={0} aria-hidden tabIndex={-1} />
)

/**
 * DS `AccordionRoot` を使わない自前の開閉 UI 用の開閉標。
 * [[AccordionDisclosureIcon]] と**見た目を揃える**ためだけに存在する。
 *
 * DS `AccordionIconButton` は `useAccordionItemContext` から開閉状態を取るので、
 * accordion context の外 (= 自前 `useState` で開閉している header) では使えない。
 * そこで DS 実装の寸法・グリフ・色をここに写す:
 *
 * - グリフ: `expanded ? FaChevronDownRegular : FaChevronRightRegular`
 *   (**回転ではなく差し替え**。 8080 も同じ)
 * - icon 20px / 器 32px / `Object.midEmphasis`
 *
 * DS 側 (`AccordionIconButton`) の寸法が変わったらここも合わせること。
 * hover 背景は付けない: 呼び出し元の header 全体が既に hover 表現を持つので、
 * 二重にかかると chevron だけ色が変わって見える。
 */
export const DisclosureIcon = ({
  expanded,
}: {
  expanded: boolean
}): React.ReactElement => (
  <Box
    as="span"
    alignItems="center"
    justifyContent="center"
    flexShrink={0}
    display="inline-flex"
    width="32px"
    height="32px"
    aria-hidden
  >
    {expanded ? (
      <FaChevronDownRegular
        boxSize={iconSize.accordionDisclosure}
        fill="Object.midEmphasis"
      />
    ) : (
      <FaChevronRightRegular
        boxSize={iconSize.accordionDisclosure}
        fill="Object.midEmphasis"
      />
    )}
  </Box>
)
